import { useCallback, useEffect, useState } from "react";
import { Download, LogOut, ExternalLink } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, formatApiErrorDetail } from "../lib/api";
import { Wordmark } from "../components/Nav";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
  } catch (e) {
    return iso || "";
  }
}

function ConsentPill({ on }) {
  return (
    <span className={"px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.14em] " + (on ? "bg-gold/20 text-navy" : "bg-navy/10 text-navy/50")}>
      {on ? "Yes" : "No"}
    </span>
  );
}

function SubmissionRow({ s }) {
  const [open, setOpen] = useState(false);
  const d = s.data || {};
  const label = d.name || d.organization || "—";
  return (
    <>
      <tr className="cursor-pointer border-b border-navy/10 align-top hover:bg-parchment" onClick={() => setOpen((o) => !o)} data-testid={"row-" + s.id}>
        <td className="px-3 py-3 text-xs text-navy/60">{fmtDate(s.created_at)}</td>
        <td className="px-3 py-3 text-sm font-medium text-navy">{label}</td>
        <td className="hidden px-3 py-3 text-sm text-navy/70 sm:table-cell">{d.email || ""}</td>
        <td className="px-3 py-3">
          {s.type === "retreat" ? <ConsentPill on={s.consent_updates} /> : s.type === "ccchaps" ? <ConsentPill on={s.consent_share} /> : <span className="text-xs text-navy/40">—</span>}
        </td>
      </tr>
      {open && (
        <tr className="border-b border-navy/10 bg-parchment/60" data-testid={"detail-" + s.id}>
          <td colSpan={4} className="px-4 py-5">
            <dl className="grid gap-x-8 gap-y-2 sm:grid-cols-2">
              {Object.entries(d).map(([k, v]) => (
                v == null ? null : (
                  <div key={k} className="flex flex-col">
                    <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-navy/50">{k.replace(/_/g, " ")}</dt>
                    <dd className="text-sm text-navy/85">{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
                  </div>
                )
              ))}
              <div className="flex flex-col">
                <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-navy/50">Submitted from IP</dt>
                <dd className="text-sm text-navy/85">{s.submitted_ip || "—"}</dd>
              </div>
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

function ListPanel({ type, consentLabel }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/submissions?type=" + type);
      setRows(data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
      setRows([]);
    }
  }, [type]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/export?type=" + type, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = type + "-submissions.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Could not export CSV. " + formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (rows === null) {
    return <p className="py-10 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-navy/50">Loading...</p>;
  }

  return (
    <div data-testid={"panel-" + type}>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-navy/60">
          {rows.length} {rows.length === 1 ? "submission" : "submissions"}
        </p>
        <button onClick={exportCsv} data-testid={"export-" + type + "-btn"} className="inline-flex items-center gap-2 border border-navy/25 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-navy transition-colors hover:border-gold hover:text-gold">
          <Download size={14} strokeWidth={1.5} />
          Export CSV
        </button>
      </div>
      {error && (
        <p role="alert" className="mb-4 border border-red-800/30 bg-red-50 px-4 py-3 text-sm text-red-900" data-testid={"panel-error-" + type}>
          {error}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="border border-navy/12 bg-parchment px-6 py-10 text-center text-sm text-navy/55" data-testid={"empty-" + type}>
          No submissions yet. New entries from the website forms will appear here.
        </p>
      ) : (
        <div className="overflow-x-auto border border-navy/12">
          <table className="w-full bg-cream text-left" data-testid={"table-" + type}>
            <thead>
              <tr className="border-b border-navy/15 bg-parchment">
                <th className="px-3 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-navy/55">Date</th>
                <th className="px-3 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-navy/55">Name</th>
                <th className="hidden px-3 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-navy/55 sm:table-cell">Email</th>
                <th className="px-3 py-3 font-mono text-[10px] uppercase tracking-[0.16em] text-navy/55">{consentLabel}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <SubmissionRow key={s.id} s={s} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-xs text-navy/45">Click a row to see full details, including consent records.</p>
    </div>
  );
}

function SettingsPanel() {
  const [email, setEmail] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/admin/settings").then((r) => setEmail(r.data.contact_email || "")).catch(() => setEmail(""));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.put("/admin/settings", { contact_email: email || "" });
      setEmail(data.contact_email || "");
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  if (email === null) return <p className="py-10 text-center font-mono text-[11px] uppercase tracking-[0.2em] text-navy/50">Loading...</p>;

  return (
    <form onSubmit={save} className="max-w-xl" data-testid="settings-form">
      <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold">Public contact details</p>
      <div className="mt-5">
        <label htmlFor="set-email" className="field-label text-navy/70">Contact email shown on the website</label>
        <input id="set-email" type="email" data-testid="settings-contact-email" className="field-input mt-1.5" placeholder="Leave empty to show no contact details" value={email} onChange={(e) => setEmail(e.target.value)} />
        <p className="mt-3 text-xs leading-relaxed text-navy/55">
          When set, this address appears in the privacy notice on the public site. Leave it empty
          to keep contact details off the website.
        </p>
      </div>
      {error && <p role="alert" className="mt-4 border border-red-800/30 bg-red-50 px-4 py-3 text-sm text-red-900" data-testid="settings-error">{error}</p>}
      <div className="mt-5 flex items-center gap-4">
        <button type="submit" disabled={busy} data-testid="settings-save-btn" className="bg-navy px-7 py-3 font-mono text-[12px] uppercase tracking-[0.2em] text-cream transition-colors hover:bg-slate2 disabled:opacity-60">
          {busy ? "Saving..." : "Save settings"}
        </button>
        {saved && <span className="text-sm text-green-800" data-testid="settings-saved">Saved.</span>}
      </div>
      <div className="mt-10 border-t border-navy/12 pt-8" data-testid="qr-section">
        <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold">Shareable interest form</p>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-navy/70">
          Scan or share this code to open the interest form at chaplainatsea.com/interest-form.
          The code keeps working after launch.
        </p>
        <img src="/chaplain-atsea-interest-qr.png" alt="QR code linking to the Chaplain at Sea interest form" className="mt-5 h-44 w-44 border border-navy/12 bg-cream p-2" data-testid="qr-image" />
        <a href="/chaplain-atsea-interest-qr.png" download data-testid="qr-download-btn" className="mt-4 inline-flex items-center gap-2 border border-navy/25 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-navy transition-colors hover:border-gold hover:text-gold">
          <Download size={14} strokeWidth={1.5} />
          Download QR code
        </a>
      </div>
    </form>
  );
}

export default function Dashboard({ admin, onLogout }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({ retreat: 0, ccchaps: 0, sponsorship: 0, quick: 0, total: 0 }));
  }, []);

  const cards = stats
    ? [
        { label: "Retreat interest", value: stats.retreat, id: "stat-retreat" },
        { label: "CCCHAPS interest", value: stats.ccchaps, id: "stat-ccchaps" },
        { label: "Sponsorship inquiries", value: stats.sponsorship, id: "stat-sponsorship" },
        { label: "Interest form", value: stats.quick, id: "stat-quick" },
        { label: "Total", value: stats.total, id: "stat-total" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-navy/12 bg-cream/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-5 py-4 sm:px-8">
          <Wordmark tone="light" />
          <div className="flex items-center gap-5">
            <span className="hidden font-mono text-[11px] text-navy/55 sm:block" data-testid="admin-email">{admin?.email}</span>
            <a href="/" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-navy/60 hover:text-gold" data-testid="admin-view-site">
              <ExternalLink size={13} strokeWidth={1.5} />
              View site
            </a>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="inline-flex items-center gap-2 border border-navy/25 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-navy transition-colors hover:border-gold hover:text-gold">
              <LogOut size={13} strokeWidth={1.5} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <h1 className="font-serif text-4xl font-light text-navy" data-testid="admin-title">Dashboard</h1>
        <p className="mt-2 text-sm text-navy/60">Interest submissions from ChaplainAtSea.com, stored separately by list.</p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {cards.map((c) => (
            <div key={c.id} className="border border-navy/12 bg-parchment p-6" data-testid={c.id}>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-navy/55">{c.label}</p>
              <p className="mt-3 font-serif text-4xl font-light text-navy">{c.value}</p>
            </div>
          ))}
        </div>

        <Tabs defaultValue="retreat" className="mt-12">
          <div className="overflow-x-auto" data-testid="admin-tabs-scroller">
          <TabsList className="h-auto w-full min-w-max justify-start gap-1 rounded-none border-b border-navy/12 bg-transparent p-0 sm:w-auto">
            <TabsTrigger value="retreat" data-testid="tab-retreat" className="rounded-none border-b-2 border-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-navy/60 data-[state=active]:border-gold data-[state=active]:text-navy">Retreat interest</TabsTrigger>
            <TabsTrigger value="ccchaps" data-testid="tab-ccchaps" className="rounded-none border-b-2 border-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-navy/60 data-[state=active]:border-gold data-[state=active]:text-navy">CCCHAPS interest</TabsTrigger>
            <TabsTrigger value="sponsorship" data-testid="tab-sponsorship" className="rounded-none border-b-2 border-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-navy/60 data-[state=active]:border-gold data-[state=active]:text-navy">Sponsorship</TabsTrigger>
            <TabsTrigger value="quick" data-testid="tab-quick" className="rounded-none border-b-2 border-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-navy/60 data-[state=active]:border-gold data-[state=active]:text-navy">Interest form</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-none border-b-2 border-transparent px-4 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-navy/60 data-[state=active]:border-gold data-[state=active]:text-navy">Settings</TabsTrigger>
          </TabsList>
          </div>
          <TabsContent value="retreat" className="mt-8"><ListPanel type="retreat" consentLabel="Updates consent" /></TabsContent>
          <TabsContent value="ccchaps" className="mt-8"><ListPanel type="ccchaps" consentLabel="Sharing consent" /></TabsContent>
          <TabsContent value="sponsorship" className="mt-8"><ListPanel type="sponsorship" consentLabel="Consent" /></TabsContent>
          <TabsContent value="quick" className="mt-8"><ListPanel type="quick" consentLabel="Contact consent" /></TabsContent>
          <TabsContent value="settings" className="mt-8"><SettingsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
